// File: frontend/src/components/marketing/visuals/MarketingDashboardVisual.js
//
// Live marketing recreation of the PesaFii dashboard: count-up metrics, a drawing
// revenue chart, router status cards, a small recent-activity feed and a wallet
// summary. Fictional data only; no API calls. Animations run once when scrolled
// into view and resolve instantly under reduced motion.

import React from "react";
import { Box, Paper, Typography, Stack, Divider, useTheme, alpha } from "@mui/material";
import { motion } from "framer-motion";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RouterIcon from "@mui/icons-material/Router";
import GroupIcon from "@mui/icons-material/Group";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import {
  MarketingMetricCard, MarketingStatusBadge, MarketingMiniLine, SampleNote,
} from "./primitives";
import { useInViewOnce, staggerContainer, fadeUp } from "../motion";
import {
  DEMO_METRICS, DEMO_REVENUE_SERIES, DEMO_TRANSACTIONS, DEMO_WALLET, DEMO_LOCATIONS, fmtUGX,
} from "../../../data/marketingDemoData";

const MoBox = motion(Box);

export default function MarketingDashboardVisual({ dense = false }) {
  const theme = useTheme();
  const [ref, inView] = useInViewOnce({ threshold: 0.25 });
  const green = theme.palette.primary.main;

  return (
    <Box ref={ref}>
      {/* Metric row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
        <MarketingMetricCard label="Revenue · month" value={DEMO_METRICS.monthlyRevenue} prefix="UGX " icon={TrendingUpIcon} color={green} active={inView} />
        <MarketingMetricCard label="Routers online" value={DEMO_METRICS.onlineRouters} suffix={` / ${DEMO_METRICS.connectedRouters}`} icon={RouterIcon} color={theme.palette.info.main} active={inView} />
        <MarketingMetricCard label="Active users" value={DEMO_METRICS.activeUsers} icon={GroupIcon} color={theme.palette.success.main} active={inView} />
        <MarketingMetricCard label="Transactions" value={DEMO_METRICS.transactionsToday} icon={SwapHorizIcon} color={theme.palette.warning.main} active={inView} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: dense ? "1fr" : "1.5fr 1fr" }, gap: 1.5 }}>
        {/* Revenue chart */}
        <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary" }}>Revenue · last 7 days</Typography>
            <MarketingStatusBadge status="online" label="Live" />
          </Stack>
          <MarketingMiniLine data={DEMO_REVENUE_SERIES.map((d) => d.v)} active={inView} color={green} height={92} />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            {DEMO_REVENUE_SERIES.map((d) => (
              <Typography key={d.d} sx={{ fontSize: 9.5, color: "text.disabled" }}>{d.d}</Typography>
            ))}
          </Stack>
        </Paper>

        {/* Wallet summary */}
        <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider", display: "flex", flexDirection: "column", justifyContent: "center", bgcolor: alpha(green, theme.palette.mode === "dark" ? 0.06 : 0.05) }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 16, color: green }} />
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 600 }}>Wallet balance</Typography>
          </Stack>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "text.primary", lineHeight: 1.1 }}>{fmtUGX(DEMO_WALLET.available)}</Typography>
          <Typography sx={{ fontSize: 11, color: "success.main", fontWeight: 700, mt: 0.5 }}>+ {fmtUGX(DEMO_WALLET.collectionsToday)} today</Typography>
        </Paper>
      </Box>

      {!dense && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mt: 1.5 }}>
          {/* Router status */}
          <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 1 }}>Routers</Typography>
            <Stack spacing={1}>
              {DEMO_LOCATIONS.slice(0, 3).map((r) => (
                <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: r.online ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: 12, color: "text.primary", fontWeight: 600 }}>{r.name}</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", flexShrink: 0 }}>{r.users} users</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          {/* Recent activity */}
          <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 1 }}>Recent activity</Typography>
            <MoBox variants={staggerContainer(0.12)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <Stack spacing={0.75} divider={<Divider sx={{ borderColor: "divider" }} />}>
                {DEMO_TRANSACTIONS.map((t) => (
                  <MoBox key={t.ref} variants={fadeUp}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 600, color: "text.primary" }}>{t.customer}</Typography>
                        <Typography sx={{ fontSize: 10, color: "text.disabled" }}>{t.ref}</Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.primary" }}>{fmtUGX(t.amount)}</Typography>
                        <MarketingStatusBadge status={t.status} />
                      </Stack>
                    </Stack>
                  </MoBox>
                ))}
              </Stack>
            </MoBox>
          </Paper>
        </Box>
      )}

      <SampleNote />
    </Box>
  );
}
